import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Trash2, ArrowLeft, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RecipeAudit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [selectedRecipes, setSelectedRecipes] = useState(new Set());

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('coach_token');
      const res = await fetch(`${API}/api/recipes/audit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAuditData(data);
      }
    } catch (err) {
      console.error('Failed to fetch audit:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudit();
  }, []);

  const handleSelectRecipe = (recipeId) => {
    const newSelected = new Set(selectedRecipes);
    if (newSelected.has(recipeId)) {
      newSelected.delete(recipeId);
    } else {
      newSelected.add(recipeId);
    }
    setSelectedRecipes(newSelected);
  };

  const handleSelectAll = () => {
    if (!auditData) return;
    const allRecipes = auditData.flagged_recipes;
    if (selectedRecipes.size === allRecipes.length) {
      setSelectedRecipes(new Set());
    } else {
      setSelectedRecipes(new Set(allRecipes.map(r => r.id)));
    }
  };

  const handleSelectAllTotal = () => {
    // Select ALL recipes in the library (not just flagged)
    if (!auditData) return;
    // This would require fetching all recipe IDs - for now use flagged
    const allFlagged = auditData.flagged_recipes;
    setSelectedRecipes(new Set(allFlagged.map(r => r.id)));
  };

  const handleDeleteSelected = async () => {
    if (selectedRecipes.size === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedRecipes.size} recipe(s)? This cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('coach_token');
      const res = await fetch(`${API}/api/recipes/bulk-delete`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(Array.from(selectedRecipes))
      });
      
      if (res.ok) {
        const result = await res.json();
        alert(`Deleted ${result.deleted_count} recipes`);
        setSelectedRecipes(new Set());
        fetchAudit(); // Refresh
      }
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete recipes');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    // First get confirmation count
    const token = localStorage.getItem('coach_token');
    const checkRes = await fetch(`${API}/api/recipes/delete-all?confirm=false`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (!checkRes.ok) {
      alert('Failed to check recipe count');
      return;
    }
    
    const checkData = await checkRes.json();
    const count = checkData.total_recipes;
    
    const confirmed = window.confirm(
      `⚠️ WARNING: This will permanently delete ALL ${count} recipes from your library!\n\nThis cannot be undone.\n\nAre you absolutely sure?`
    );
    
    if (!confirmed) return;
    
    // Double confirm
    const doubleConfirm = window.confirm(
      `Final confirmation: Delete all ${count} recipes?\n\nType "DELETE" in the next prompt to confirm.`
    );
    
    if (!doubleConfirm) return;
    
    const typed = window.prompt('Type DELETE to confirm:');
    if (typed !== 'DELETE') {
      alert('Deletion cancelled');
      return;
    }
    
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/recipes/delete-all?confirm=true`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        alert(`Deleted ${result.deleted_count} recipes`);
        fetchAudit(); // Refresh
      }
    } catch (err) {
      console.error('Failed to delete all:', err);
      alert('Failed to delete recipes');
    } finally {
      setDeleting(false);
    }
  };

  const getFilteredRecipes = () => {
    if (!auditData) return [];
    return auditData.flagged_recipes;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-gray-500">Loading audit...</div>
      </div>
    );
  }

  const filteredRecipes = getFilteredRecipes();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <h1 className="text-2xl font-semibold text-gray-900">Recipe Audit</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchAudit}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={handleDeleteAll}
              disabled={deleting || !auditData?.total_recipes}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete All Recipes
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {auditData && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{auditData.total_recipes}</div>
                <div className="text-sm text-gray-500">Total Recipes</div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{auditData.flagged_count}</div>
                <div className="text-sm text-gray-500">Flagged</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">{auditData.summary?.diet_contradictions || 0}</div>
                <div className="text-sm text-gray-500">Diet Issues</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{auditData.summary?.macro_mismatches || 0}</div>
                <div className="text-sm text-gray-500">Macro Issues</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Bar */}
        {filteredRecipes.length > 0 && (
          <div className="flex items-center justify-between bg-white rounded-lg border p-3 mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <Checkbox 
                checked={selectedRecipes.size === filteredRecipes.length && filteredRecipes.length > 0}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm text-gray-600">
                {selectedRecipes.size > 0 
                  ? `${selectedRecipes.size} selected`
                  : `${filteredRecipes.length} flagged recipes`
                }
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedRecipes.size === filteredRecipes.length ? 'Deselect All' : 'Select All Flagged'}
              </Button>
            </div>
            {selectedRecipes.size > 0 && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handleDeleteSelected}
                disabled={deleting}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deleting ? 'Deleting...' : `Delete ${selectedRecipes.size}`}
              </Button>
            )}
          </div>
        )}

        {/* Recipe List */}
        {filteredRecipes.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              {auditData?.total_recipes === 0 
                ? "No recipes in your library yet."
                : "No issues found! Your recipes look good."
              }
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredRecipes.map((recipe) => (
              <Card 
                key={recipe.id} 
                className={`${selectedRecipes.has(recipe.id) ? 'border-blue-500 bg-blue-50' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      checked={selectedRecipes.has(recipe.id)}
                      onCheckedChange={() => handleSelectRecipe(recipe.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <span className="font-medium text-gray-900">{recipe.name}</span>
                        <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                          {recipe.dietary_category}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-gray-50">
                          {recipe.meal_type}
                        </Badge>
                      </div>
                      
                      {/* Macros */}
                      <div className="text-xs text-gray-500 mb-2">
                        {recipe.total_kcal?.toFixed(0)} kcal • P: {recipe.total_protein?.toFixed(0)}g • C: {recipe.total_carbs?.toFixed(0)}g • F: {recipe.total_fat?.toFixed(0)}g
                      </div>
                      
                      {/* Ingredients preview */}
                      {recipe.ingredients && recipe.ingredients.length > 0 && (
                        <div className="text-xs text-gray-400 mb-2">
                          Ingredients: {recipe.ingredients.join(", ")}
                        </div>
                      )}
                      
                      {/* Issues */}
                      <div className="space-y-1">
                        {recipe.issues.map((issue, idx) => (
                          <div 
                            key={idx} 
                            className="text-sm text-red-600 font-medium"
                          >
                            ⚠ {issue.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Legend */}
        <Card className="mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700">What gets flagged?</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-600 space-y-2">
            <div><strong>Diet contradictions:</strong> Halal with pork, vegan with animal products, dairy-free with cheese, vegetarian with meat/fish, gluten-free with wheat, etc.</div>
            <div><strong>High-protein issues:</strong> Recipes labeled "high-protein" where carbs exceed protein</div>
            <div><strong>Keto issues:</strong> Recipes with 20g+ carbs, or relying on cooking oils for fat scaling</div>
            <div><strong>Low-carb issues:</strong> Recipes labeled "low-carb" with 30g+ carbs</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
